/* ============================================================
   KoffyFinder quiz — top-3 beans + matched coffret.
   Questions come from an inline <script data-kl-quiz-data> blob.
   Beans + coffrets come from assets/kl-quiz-data.json (data-quiz-src).
   Result display is enriched per-item via /products/<handle>.js.
   ============================================================ */
import { computeDesired, rankBeans, pickCoffret } from './kl-quiz-scoring.js';

const CART_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="16" height="16"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>';

function euro(v) {
  const n = parseFloat(v);
  return isNaN(n) ? '' : n.toFixed(2).replace('.', ',') + ' €';
}

function pushQuizEvent(event, detail) {
  const payload = { event, ...detail };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  document.dispatchEvent(new CustomEvent(event, { detail: payload }));
}

// Daily rotation: deterministically reorder the bean/coffret pool per day (Europe/Paris) so the
// quiz surfaces different *equally-good* matches each day. Ranking is unchanged — rankBeans sorts
// by match score with a stable sort, so this only rotates ties + the one-per-roaster pick.
function klQuizDay() {
  try {
    const s = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });
    const p = s.split('-');
    return Math.floor(Date.UTC(+p[0], +p[1] - 1, +p[2]) / 86400000);
  } catch (e) { return Math.floor(Date.now() / 86400000); }
}
function klMulberry32(a) {
  return function () { a |= 0; a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function klDailyShuffle(arr) {
  const a = (arr || []).slice();
  const rnd = klMulberry32((klQuizDay() * 2654435761) >>> 0);
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); const t = a[i]; a[i] = a[j]; a[j] = t; }
  return a;
}

async function enrich(item) {
  // Fetch live image, price and first available variant id from the Ajax product API.
  try {
    const r = await fetch(`/products/${item.handle}.js`, { headers: { Accept: 'application/json' } });
    if (!r.ok) return item;
    const p = await r.json();
    const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
    return {
      ...item,
      img: item.img || (p.featured_image || (p.media && p.media[0] && p.media[0].preview_image && p.media[0].preview_image.src)) || '',
      price: v ? (v.price / 100).toString() : item.price,
      variantId: v ? v.id : null,
      available: v ? v.available : item.available,
    };
  } catch (e) { return item; }
}

async function addToCart(variantId, btn, context = {}) {
  const original = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '…';
  // Mirror the theme's add flow so the cart drawer re-renders AND opens (Section Rendering API).
  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  const body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };
  if (cart && typeof cart.getSectionsToRender === 'function') {
    body.sections = cart.getSectionsToRender().map((s) => s.id);
    body.sections_url = window.location.pathname;
  }
  try {
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then((r) => r.json());
    if (res.status) { btn.disabled = false; btn.innerHTML = original; return; } // add error (e.g. sold out)
    if (cart && typeof cart.renderContents === 'function') {
      cart.classList.remove('is-empty');
      cart.renderContents(res); // updates drawer + cart bubble and opens the drawer
    } else if (typeof window.openCartDrawer === 'function') {
      window.openCartDrawer();
    }
    // keep any custom header counter in sync too
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((c) => { document.querySelectorAll('[data-kl-cart-count]').forEach((el) => { el.textContent = String(c.item_count || 0); }); })
      .catch(() => {});
    btn.innerHTML = 'Ajouté ✓';
    pushQuizEvent('koffyfinder_add_to_cart', {
      item_handle: context.handle || '',
      item_type: context.type || 'coffee',
    });
  } catch (e) {
    btn.disabled = false;
    btn.innerHTML = original;
  }
}

function beanCard(b, rank, pct) {
  const el = document.createElement('div');
  el.className = 'kl-quiz__bean' + (rank === 1 ? ' is-top' : '');
  const badge = rank === 1
    ? `<span class="kl-quiz__rank is-top">★ Meilleur match${pct ? ' · ' + pct + '%' : ''}</span>`
    : `<span class="kl-quiz__rank">${rank}ᵉ${pct ? ' · ' + pct + '%' : ''}</span>`;
  const chips = (b.profile || []).slice(0, 3).map((t) => `<span>${t}</span>`).join('');
  el.innerHTML = `
    ${badge}
    <a class="kl-quiz__bean-media" href="${b.url}">
      ${b.img ? `<img src="${b.img}" alt="${b.title}" loading="lazy" width="200" height="200">` : `<span class="kl-quiz__bag">${b.title}</span>`}
    </a>
    <div class="kl-quiz__bean-body">
      <span class="kl-quiz__roaster">${b.roaster || ''}</span>
      <a class="kl-quiz__bean-name" href="${b.url}">${b.title}</a>
      ${rank === 1 ? '<p class="kl-quiz__bean-note">Votre meilleure correspondance, d\'après vos réponses.</p>' : ''}
      <div class="kl-quiz__chips">${chips}</div>
      <div class="kl-quiz__bean-foot">
        <span class="kl-quiz__price">${euro(b.price)}</span>
        <button type="button" class="kl-btn kl-btn-primary kl-quiz__add">${CART_ICON} Ajouter</button>
      </div>
    </div>`;
  const btn = el.querySelector('.kl-quiz__add');
  enrich(b).then((full) => {
    if (full.img) {
      const media = el.querySelector('.kl-quiz__bean-media');
      media.innerHTML = `<img src="${full.img}" alt="${full.title}" loading="lazy" width="200" height="200">`;
    }
    el.querySelector('.kl-quiz__price').textContent = euro(full.price);
    btn.addEventListener('click', () => {
      if (full.variantId) addToCart(full.variantId, btn, { handle: b.handle, type: 'coffee' }); else window.location.href = b.url;
    });
  });
  return el;
}

function coffretCard(c, beans) {
  const el = document.createElement('div');
  el.className = 'kl-quiz__coffret';
  const memberNames = (c.members || [])
    .map((h) => { const b = (beans || []).find((x) => x.handle === h); return b ? b.title.split(' – ')[0].split(',')[0] : null; })
    .filter(Boolean);
  const originalSum = (c.members || []).reduce((sum, h) => {
    const b = (beans || []).find((x) => x.handle === h);
    return sum + (b ? parseFloat(b.price) : 0);
  }, 0);
  el.innerHTML = `
    <div class="kl-quiz__coffret-media">
      <span class="kl-quiz__coffret-save">−10 % en coffret</span>
    </div>
    <div class="kl-quiz__coffret-body">
      <span class="kl-quiz__coffret-tag">★ Coffret recommandé pour vous</span>
      <h4 class="kl-quiz__coffret-name">${c.title}</h4>
      <p class="kl-quiz__coffret-sub">Trois cafés sélectionnés selon votre profil, réunis pour explorer sans se ruiner — et repartir avec votre prochain favori.</p>
      <div class="kl-quiz__coffret-members">${memberNames.map((n) => `<span>${n}</span>`).join('')}</div>
      <div class="kl-quiz__coffret-prices">
        ${originalSum ? `<span class="kl-quiz__coffret-was">${euro(originalSum)}</span>` : ''}
        <span class="kl-quiz__price" data-coffret-price></span>
      </div>
      <div class="kl-quiz__coffret-buy">
        <a class="kl-btn kl-btn-primary kl-quiz__add" href="${c.url}">${CART_ICON} Voir le coffret</a>
      </div>
    </div>`;
  // Only show the coffret if it is actually published/purchasable; drafts 404 on the Ajax API.
  fetch(`/products/${c.handle}.js`, { headers: { Accept: 'application/json' } })
    .then((r) => { if (!r.ok) throw new Error('unavailable'); return r.json(); })
    .then((p) => {
      const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
      el.querySelector('[data-coffret-price]').textContent = v ? euro(v.price / 100) : '';
      const img = p.featured_image || (p.media && p.media[0] && p.media[0].preview_image && p.media[0].preview_image.src);
      if (img) el.querySelector('.kl-quiz__coffret-media').insertAdjacentHTML('afterbegin', `<img src="${img}" alt="${(p.title || '').replace(/"/g, '')}">`);
      const btn = el.querySelector('.kl-quiz__add');
      if (v && v.available) {
        btn.removeAttribute('href');
        btn.innerHTML = `${CART_ICON} Ajouter le coffret au panier`;
        btn.addEventListener('click', () => addToCart(v.id, btn, { handle: c.handle, type: 'coffret' }));
      }
    })
    .catch(() => { el.remove(); });
  return el;
}

function initQuiz(section) {
  if (!section || section.__klQuizReady) return;
  const dataEl = section.querySelector('[data-kl-quiz-data]');
  if (!dataEl) return;
  let CONF;
  try { CONF = JSON.parse(dataEl.textContent); } catch (e) { return; }
  const QUESTIONS = CONF.questions || [];
  if (!QUESTIONS.length) return;

  const card = section.querySelector('[data-quiz-card]');
  const optionsEl = section.querySelector('[data-quiz-options]');
  const questionEl = section.querySelector('[data-quiz-question]');
  const progressEl = section.querySelector('[data-quiz-progress]');
  const stepLabelEl = section.querySelector('[data-quiz-step-label]');
  const answeredEl = section.querySelector('[data-quiz-answered]');
  const backBtn = section.querySelector('[data-quiz-back]');
  const startBtn = section.querySelector('[data-quiz-start]');
  const restartBtn = section.querySelector('[data-quiz-restart]');
  const viewQuestion = section.querySelector('[data-quiz-view="question"]');
  const viewResult = section.querySelector('[data-quiz-view="result"]');
  const beansEl = section.querySelector('[data-quiz-beans]');
  const coffretEl = section.querySelector('[data-quiz-coffret]');
  const profileEl = section.querySelector('[data-quiz-profile]');
  const leadForm = section.querySelector('.kl-quiz__lead-form');
  const leadEmail = section.querySelector('[data-quiz-lead-email]');
  const leadSubmit = section.querySelector('[data-quiz-lead-submit]');
  const leadStatus = section.querySelector('[data-quiz-lead-status]');
  const leadTags = section.querySelector('[data-quiz-lead-tags]');
  const stepCards = section.querySelectorAll('[data-quiz-step-card]');
  if (!card || !optionsEl) return;

  let DATA = { beans: [], coffrets: [] };
  const src = section.getAttribute('data-quiz-src');
  const dataPromise = src
    ? fetch(src, { headers: { Accept: 'application/json' } })
      .then((r) => {
        if (!r.ok) throw new Error('quiz-data');
        return r.json();
      })
      .then((d) => {
        DATA = d;
        DATA.beans = klDailyShuffle(DATA.beans);
        DATA.coffrets = klDailyShuffle(DATA.coffrets);
      })
      .catch(() => {})
    : Promise.resolve();

  const fresh = () => ({ step: 0, answers: new Array(QUESTIONS.length).fill(null), finished: false });
  let state = fresh();
  let finishing = false;

  function renderStepMap() {
    Array.prototype.forEach.call(stepCards, (el) => {
      const n = parseInt(el.getAttribute('data-quiz-step-card'), 10);
      let status = 'pending';
      if (state.finished || n - 1 < state.step) status = 'done';
      else if (n - 1 === state.step) status = 'current';
      el.setAttribute('data-quiz-step-status', status);
    });
  }

  function render() {
    card.setAttribute('data-quiz-step', String(state.step + 1));
    card.setAttribute('data-quiz-state', state.finished ? 'result' : 'question');
    if (state.finished) {
      if (viewQuestion) viewQuestion.setAttribute('hidden', '');
      if (viewResult) viewResult.removeAttribute('hidden');
      renderStepMap();
      return;
    }
    if (viewResult) viewResult.setAttribute('hidden', '');
    if (viewQuestion) viewQuestion.removeAttribute('hidden');
    const q = QUESTIONS[state.step];
    if (questionEl) questionEl.textContent = q.question;
    if (stepLabelEl) stepLabelEl.textContent = 'Question ' + (state.step + 1) + ' sur ' + QUESTIONS.length;
    const pct = Math.round(((state.step + 1) / QUESTIONS.length) * 100);
    if (progressEl) progressEl.style.width = pct + '%';
    const answered = state.answers.filter((a) => a !== null).length;
    if (answeredEl) answeredEl.textContent = answered + (answered <= 1 ? ' réponse' : ' réponses');
    if (backBtn) backBtn.disabled = state.step === 0;
    optionsEl.innerHTML = '';
    const current = state.answers[state.step];
    (q.options || []).forEach((opt) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      const sel = current && current.key === opt.key;
      btn.className = 'quiz-option' + (sel ? ' is-selected' : '');
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', sel ? 'true' : 'false');
      btn.innerHTML = `<span class="quiz-option-key">${opt.key}</span><span>${opt.label}</span>`;
      btn.addEventListener('click', () => selectOption(opt));
      optionsEl.appendChild(btn);
    });
    renderStepMap();
  }

  function selectOption(opt) {
    state.answers[state.step] = opt;
    render();
    if (state.step < QUESTIONS.length - 1) window.setTimeout(() => { state.step += 1; render(); }, 220);
    else window.setTimeout(finish, 220);
  }

  function back() {
    if (state.step === 0 && !state.finished) return;
    if (state.finished) state.finished = false; else state.step -= 1;
    render();
  }

  async function finish() {
    if (finishing) return;
    finishing = true;
    card.setAttribute('aria-busy', 'true');
    await dataPromise;
    const desired = computeDesired(state.answers.map((a) => (a ? a.tags : [])));
    const top = rankBeans(DATA.beans, desired, 3);
    const coffret = pickCoffret(DATA.coffrets, desired);
    const profileTags = Object.keys(desired).sort((a, b) => desired[b] - desired[a]).slice(0, 4);
    state.finished = true;
    render();
    if (profileEl) {
      profileEl.innerHTML = '<span>Votre profil :</span>' + profileTags.map((t) => `<span class="is-tag">${t}</span>`).join('');
    }
    if (leadTags) {
      const baseLeadTags = leadTags.dataset.quizLeadBaseTags || 'newsletter, koffyfinder, quiz-result';
      leadTags.value = [baseLeadTags].concat(profileTags.map((t) => `profil-${t}`)).join(', ');
    }
    if (beansEl) { beansEl.innerHTML = ''; top.forEach((b, i) => beansEl.appendChild(beanCard(b, i + 1, 96 - i * 4))); }
    if (coffretEl) { coffretEl.innerHTML = ''; if (coffret) coffretEl.appendChild(coffretCard(coffret, DATA.beans)); }
    pushQuizEvent('koffyfinder_complete', {
      profile: profileTags.join('|'),
      top_match: top[0] ? top[0].handle : '',
      coffret: coffret ? coffret.handle : '',
    });
    card.removeAttribute('aria-busy');
    finishing = false;
  }

  if (leadForm) {
    leadForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!leadEmail || !leadEmail.checkValidity()) {
        leadEmail?.reportValidity();
        return;
      }
      if (leadSubmit) {
        leadSubmit.disabled = true;
        leadSubmit.textContent = 'Envoi…';
      }
      if (leadStatus) leadStatus.hidden = true;
      try {
        const response = await fetch(leadForm.action, {
          method: 'POST',
          body: new FormData(leadForm),
          headers: { Accept: 'text/html' },
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error('newsletter');
        const responseHtml = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');
        const returnedForm = responseDocument.getElementById(leadForm.id);
        const returnedResult = returnedForm?.querySelector('[data-quiz-lead-result]');
        if (!returnedResult || returnedResult.dataset.quizLeadResult !== 'success') {
          const returnedError = returnedResult?.textContent?.trim();
          throw new Error(returnedError || 'newsletter');
        }
        leadForm.classList.add('is-complete');
        leadEmail.disabled = true;
        if (leadSubmit) leadSubmit.hidden = true;
        if (leadStatus) {
          leadStatus.textContent = 'Merci, votre sélection est enregistrée.';
          leadStatus.hidden = false;
        }
        pushQuizEvent('koffyfinder_lead', { source: 'quiz-result' });
      } catch (error) {
        if (leadSubmit) {
          leadSubmit.disabled = false;
          leadSubmit.textContent = 'Recevoir les conseils';
        }
        if (leadStatus) {
          leadStatus.textContent = error.message && error.message !== 'newsletter'
            ? error.message
            : 'L’inscription n’a pas abouti. Réessayez dans un instant.';
          leadStatus.hidden = false;
        }
      }
    });
  }

  if (backBtn) backBtn.addEventListener('click', back);
  if (restartBtn) restartBtn.addEventListener('click', () => { state = fresh(); render(); });
  if (startBtn) startBtn.addEventListener('click', (e) => {
    e.preventDefault();
    state = fresh();
    render();
    card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  render();
  section.__klQuizReady = true;
  section.setAttribute('data-quiz-ready', 'true');
}

function boot() {
  document.querySelectorAll('[data-kl-quiz]').forEach(initQuiz);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', (e) => {
  const s = e.target.querySelector('[data-kl-quiz]');
  if (s) { s.__klQuizReady = false; initQuiz(s); }
});
